import React from 'react';

import './OrderDetail.scss';
import { I18NContext } from '../../I18NContext';
import Help from '../Help';
import { Alert, CancellationResult, MedicationGroup, OrderSummaryDetails, OrderSummaryPage, PatientDrugRegimenType, DigitalShiplitPageContext } from '../component.d';
import MedicationCardChargeLine from '../common/MedicationCardChargeLine';
import HeavyDivider from '../common/HeavyDivider';
import { OrderDetailBreadCrumb } from '../common/BreadCrumb';
import {hasAnnotation} from '../common/PaymentUtils';
import { PaymentDeclineAlert } from '../common/PaymentDeclineAlert';
import {AboveTheFoldMarker, FunctionalMarker} from "../csa/markers";
import OrderSummaryRow from "../OrderSummaryRow";
import CancelV2 from '../common/CancelV2';
import RefundRowV2 from './RefundRowV2';
import { ViewInvoiceMenu } from '../InvoiceMenu';
import {GroupType} from "../../common/Types";
import PillPackMedicationGroupAccordionCard from "../PillPackMedicationGroupAccordionCard";
import { generateMedSyncAnnotationAlert } from '../common/AnnotationUtils';
import MedicationCard from "../common/MedicationCard";
import PaymentV2 from "../common/PaymentV2";
import MedicationCardAutoRefillLine from "../common/MedicationCardAutoRefillLine";
import MedicationCardPromotionLine from "../common/MedicationCardPromotionLine";
import ExternalLink from '../common/ExternalLink';
import MedicationGroupAccordionAlertMessage from '../medicationGroup/MedicationGroupAccordionAlertMessage';
import GenericAlert from '../common/GenericAlert';
import { generateCsaTagProps, generateCsaTagSuffix, generateDeliveryCardCsaTag } from "../csa/utils";
import DigitalShiplitLink from "../order/DigitalShiplitLink";
import DigitalShiplitBottomSheet from "./DigitalShiplitBottomSheet";
import MedicationCardManufacturerChangedLine from "../common/MedicationCardManufacturerChangedLine";

interface OrderDetailV2Props {
    urlId: string;
    orderSummaryPage: OrderSummaryPage;
    paymentMethodUpdated?: boolean;
    cancellationResult?: CancellationResult;
    onCancellationComplete?: (result: CancellationResult) => void;
}

class OrderDetailV2 extends React.Component<OrderDetailV2Props, {}>  {
    private bottomSheetRef = React.createRef<any>();

    handleShiplitClick = (orderKeyId: string) => {
        this.bottomSheetRef.current?.showBottomSheet(orderKeyId);
    };

    private getDigitalShiplitLinkProps = () => ({
        labels: {
            viewShipmentLiterature: this.props.orderSummaryPage.orderSummaryPageLabels.viewShipmentLiterature,
            downloadingShipmentLiterature: this.props.orderSummaryPage.orderSummaryPageLabels.downloadingShipmentLiterature,
            downloadingErrorShipmentLiterature: this.props.orderSummaryPage.orderSummaryPageLabels.downloadingErrorShipmentLiterature,
            medicationInformationShipmentLiterature: this.props.orderSummaryPage.orderSummaryPageLabels.medicationInformationShipmentLiterature,
            downloadingDoneShipmentLiterature: this.props.orderSummaryPage.orderSummaryPageLabels.downloadingDoneShipmentLiterature,
            continueShipmentLiterature: this.props.orderSummaryPage.orderSummaryPageLabels.continueShipmentLiterature,
            pdfNameShipmentLiterature: this.props.orderSummaryPage.orderSummaryPageLabels.pdfNameShipmentLiterature
        },
        onShiplitClick: this.handleShiplitClick
    });
	 
    render() {
        const { urlId , orderSummaryPage, paymentMethodUpdated } = this.props;
        /**
        * For PillPack order, orderSummaryPage.orders can contains two or more orders.
         * For the other Order, orderSummaryPage.orders only contains one order.
        */

        const canCancel = hasAnnotation(
            orderSummaryPage?.cancelDetails?.cancellationGroup?.annotations || [],
            'Cancel'
        );
        const isPillPack = (orderSummaryPage.medicationGroupCard?.medicationGroupList?.length ?? 0) > 0;
        return (
          <pui-stack data-csa-c-content-id={`order-detail-page${generateCsaTagSuffix(isPillPack)}`}
                     data-csa-c-type="card"
                     data-csa-c-slot-id="order-detail">
                    {this._generateAlerts(orderSummaryPage, urlId, paymentMethodUpdated || false)}
                    <OrderDetailBreadCrumb
                        yourOrders={orderSummaryPage.orderSummaryPageLabels.yourOrders}
                        delivery={orderSummaryPage.orderSummaryPageLabels.delivery}
                        tracking={orderSummaryPage.orderSummaryPageLabels.tracking}
                        order={orderSummaryPage.orderSummaryPageLabels.order}
                        bypassTracker={orderSummaryPage.bypassTracker}
                        isPickupOrder={orderSummaryPage.isPickUpOrder}
                    />
                    <pui-heading input={orderSummaryPage.orderSummaryPageLabels.orderSummary} textSize='large' spacingTop="small" spacingBottom="small"/>
                    {this._generateOrderSummaryTable(orderSummaryPage.orderSummaryDetails)}
                    <RefundRowV2 refundDetails={orderSummaryPage.refundDetails?.refundDetails} refundBottomSheetDetails={orderSummaryPage.refundDetails?.refundBottomSheetDetails}></RefundRowV2>

                    <HeavyDivider />
                    {this._generateMedicationGroupAccordionCard(orderSummaryPage)}
                    <AboveTheFoldMarker/>
                    {orderSummaryPage.shiplitIds && orderSummaryPage.shiplitIds.length > 0 && 
	                    orderSummaryPage.shiplitIds.map((shiplitId, index) => (
	                        <DigitalShiplitLink
	                            key={`shiplit-link-${index}`}
	                            orderKeyId={shiplitId}
	                            currentLinkNumber={orderSummaryPage.shiplitIds!.length > 1 ? index + 1 : undefined}
	                            {...this.getDigitalShiplitLinkProps()}
	                         />
	                    ))
	                 }
                    <HeavyDivider />
                    <PaymentV2
                        paymentInformation={orderSummaryPage.paymentInformation}
                        insuranceInformation={orderSummaryPage.insuranceInformation}
                        paymentMethodLabel={orderSummaryPage.orderSummaryPageLabels.paymentMethod}
                        paymentInfoHeader={orderSummaryPage.orderSummaryPageLabels.paymentInformation}
                        updatePaymentMethod={orderSummaryPage.orderSummaryPageLabels.updatePaymentMethod}
                        paymentLabels={orderSummaryPage.orderSummaryPageLabels.paymentLabels}
                        insuranceAndDiscountCardInformation={orderSummaryPage.orderSummaryPageLabels.insuranceAndDiscountCardInformation}
                        isAddPaymentMethodAllowed={orderSummaryPage.isAddPaymentMethodAllowed}
                        /**
                         * TODO: replace with payment gqlorderid's
                         */
                        isPillPack={isPillPack}
                        isPillPackCom={false}
                        urlId={urlId}
                    />
                    <HeavyDivider />
                <pui-heading textSize="medium" spacingBottom="small"
                             input={orderSummaryPage.orderSummaryPageLabels.help}/>
                <Help invoiceId={urlId}
                      viewInvoice={orderSummaryPage.orderSummaryPageLabels.viewInvoice}
                      managePillpackSettings={orderSummaryPage.orderSummaryPageLabels.viewPillpackSettings}
                      contactCustomerCare={orderSummaryPage.orderSummaryPageLabels.contactCustomerCare}
                      isPillPack={isPillPack}>
                    {canCancel && <CancelV2 cancelDetails={this.props.orderSummaryPage.cancelDetails}
                                            orderSummaryPageLabels={this.props.orderSummaryPage.orderSummaryPageLabels}
                                            isPillPack={isPillPack}
                                            onCancellationComplete={this.props.onCancellationComplete}/>}
                    {isPillPack && <ViewInvoiceMenu invoiceMenu={orderSummaryPage.invoiceMenu}
                                                        title={orderSummaryPage.orderSummaryPageLabels.invoiceMenuTitle}
                                                        buttonName={orderSummaryPage.orderSummaryPageLabels.viewInvoiceMenu}/>}
                    </Help>
                {/* Single bottom sheet instance */}
                <DigitalShiplitBottomSheet
                    bottomSheetRef={this.bottomSheetRef}
                    labels={{
                        viewShipmentLiterature: this.props.orderSummaryPage.orderSummaryPageLabels.viewShipmentLiterature,
                        downloadingShipmentLiterature: this.props.orderSummaryPage.orderSummaryPageLabels.downloadingShipmentLiterature,
                        downloadingErrorShipmentLiterature: this.props.orderSummaryPage.orderSummaryPageLabels.downloadingErrorShipmentLiterature,
                        medicationInformationShipmentLiterature: this.props.orderSummaryPage.orderSummaryPageLabels.medicationInformationShipmentLiterature,
                        downloadingDoneShipmentLiterature: this.props.orderSummaryPage.orderSummaryPageLabels.downloadingDoneShipmentLiterature,
                        continueShipmentLiterature: this.props.orderSummaryPage.orderSummaryPageLabels.continueShipmentLiterature,
                        pdfNameShipmentLiterature: this.props.orderSummaryPage.orderSummaryPageLabels.pdfNameShipmentLiterature

                    }}
                    isIosDevice={this.props.orderSummaryPage.isIosDevice}
                    isAndroidDevice={this.props.orderSummaryPage.isAndroidDevice}
                    isMShop={this.props.orderSummaryPage.isMShop}
                    currentPage={DigitalShiplitPageContext.OrderSummary}

                />
                    <FunctionalMarker/>
            </pui-stack>);
    }
    _generateOrderSummaryTable(orderSummaryDetails: OrderSummaryDetails) {
        const charge = orderSummaryDetails.charges;
        return (
            <pui-stack>
                <table style={{ width: '100%' }}>
                    <tbody>
                        <OrderSummaryRow label={orderSummaryDetails.idLabel} value={orderSummaryDetails.id} />
                        <OrderSummaryRow label={orderSummaryDetails.dateLabel} value={orderSummaryDetails.date} />
                        <OrderSummaryRow label={charge.itemLabel} value={charge.item} />
                        { charge.couponSaving && <OrderSummaryRow label={charge.couponSavingLabel} value={charge.couponSaving} />}
                        <OrderSummaryRow label={charge.unitPrice} value={charge.unitPrice} />
                        <OrderSummaryRow label={charge.shippingLabel} value={charge.shipping} />
                        { charge.shippingSaving && <OrderSummaryRow label={charge.shippingSavingLabel} value={charge.shippingSaving} />}
                        { charge.pillpackServiceFee && <OrderSummaryRow label={charge.pillpackServiceFeeLabel} value={charge.pillpackServiceFee} />}
                        <OrderSummaryRow label={charge.totalBeforeTaxLabel} value={charge.totalBeforeTax} />
                        <OrderSummaryRow label={charge.salesTaxLabel} value={charge.salesTax} />
                        <OrderSummaryRow label={charge.hbaEligibleTotalLabel} value={charge.hbaEligibleTotal} />
                        <OrderSummaryRow label={charge.grandTotalLabel} value={charge.grandTotal} fontWeight="bold" />
                    </tbody>
                </table>
            </pui-stack>
        )
    }
    /**
     * Generates the accordion card for the order.
     */
    _generateMedicationGroupAccordionCard(orderSummaryPage: OrderSummaryPage) {
        const medicationGroupCard = orderSummaryPage.medicationGroupCard;
        const medicationGroupCardHeader = medicationGroupCard?.medicationGroupHeader;
        if (medicationGroupCardHeader == null) {
            return <></>;
        }
        const headline = medicationGroupCard.medicationGroupHeader.headline;
        const subline = medicationGroupCard.medicationGroupHeader.subline;

        const alert = generateMedSyncAnnotationAlert(medicationGroupCardHeader.annotations);
        if (!medicationGroupCard.medicationGroupList || medicationGroupCard.medicationGroupList.length === 0) {
            return (
                <ExternalLink href={`/dp/${medicationGroupCardHeader.patientDrugRegimenType === PatientDrugRegimenType.NON_PRESCRIPTION 
                    ? (medicationGroupCardHeader.asin) 
                    : (medicationGroupCardHeader.drugId || medicationGroupCardHeader.asin)}`} key={medicationGroupCardHeader.id}>
                    <MedicationCard
                        headline={headline}
                        subline={subline}
                        imageURL={medicationGroupCardHeader.photoUrl}
                    >
                        <MedicationCardAutoRefillLine
                            isAutoRefill={hasAnnotation(medicationGroupCardHeader.annotations, 'AutoRefill')}
                            autoRefillTextDisplay={medicationGroupCardHeader.autoRefillLine}
                        />

                        {medicationGroupCardHeader.priceString && (
                            <MedicationCardChargeLine chargeAmount={medicationGroupCardHeader.priceString}/>
                        )}

                        <MedicationCardManufacturerChangedLine
                            hasManufacturerChanged={hasAnnotation(medicationGroupCardHeader.annotations,
                                'ManufacturerChanged')}
                            hasManufacturerChangedLabel={medicationGroupCardHeader.annotations
                                .find(annotation =>
                                    annotation.annotationType === 'ManufacturerChanged')?.label || ''}
                        />

                        {medicationGroupCardHeader.paymentInformation && (
                            <pui-text textSize="small" input={medicationGroupCardHeader.paymentInformation}/>
                        )}

                        {medicationGroupCardHeader.promotionSavingLine && (
                            <MedicationCardPromotionLine promotionSavingsLine={medicationGroupCardHeader.promotionSavingLine} />
                        )}
                    </MedicationCard>
                    {alert && <MedicationGroupAccordionAlertMessage {...alert} csaTagProps={{contentId:"med-sync",slotId:"med-sync",type:"alert"}}/>}
                </ExternalLink>
            )
        }
        const headerProps = {
            pillPack: {
              heading: headline,
              subHeading: subline,
              groupType: GroupType.PILLPACK,
              children: medicationGroupCardHeader.priceString ? (
                <MedicationCardChargeLine chargeAmount={medicationGroupCardHeader.priceString} />
              ) : null,
            },
        };

        const medicationCards = {
            pillPack: this._generateMedicationGroupCard(medicationGroupCard.medicationGroupList)
        }
        return <PillPackMedicationGroupAccordionCard
            id={`accordion${orderSummaryPage.orderSummaryPageLabels.id}`}
                                                     groupAlertMessage={alert}
                                                     medicationCards={medicationCards}
                                                     headerProps={headerProps} 
                                                     showDivider={false}
                                                     marginLeft={true}
                                                     noCollapse={true}/>
    }

    _generateMedicationCardChargeLine(medicationGroupCardHeader: MedicationGroup) {
        return medicationGroupCardHeader.priceString ? (
            <MedicationCardChargeLine chargeAmount={medicationGroupCardHeader.priceString} />
          ) : null;
    }

    _generateMedicationGroupCard(medicationGroupList: MedicationGroup[]) {
        return (
            <>
                {medicationGroupList.map(medication => (
                    <ExternalLink href={`/dp/${medication.patientDrugRegimenType === PatientDrugRegimenType.PRESCRIPTION 
                        ? (medication.drugId || medication.asin) 
                        : (medication.asin)}`} key={medication.id}>
                        <MedicationCard
                            headline={medication.headline}
                            subline={medication.subline}
                            imageURL={medication.photoUrl}
                            promotionSavingLine={medication.promotionSavingLine}
                            annotations={medication.annotations}
                        >
                        </MedicationCard>
                    </ExternalLink>
                ))}
            </>
        )
    }

    _generateAlerts = (orderSummaryPage: OrderSummaryPage, gqlOrderId: string, paymentMethodUpdated: boolean) => {
        const alerts = [];
        const isPillPack = (orderSummaryPage.medicationGroupCard?.medicationGroupList?.length ?? 0) > 0;
        const pillPackSuffix = generateCsaTagSuffix(isPillPack);

        // Cancellation banner renders first for accessibility (screen reader announcement)
        if (this.props.cancellationResult) {
            const cancelForm = orderSummaryPage.orderSummaryPageLabels.cancelForm;
            if (this.props.cancellationResult.status === 'success') {
                const count = this.props.cancellationResult.cancelledCount;
                const headline = count === 1
                    ? `${count} ${cancelForm.cancellationSuccessHeadlineSingular}`
                    : `${count} ${cancelForm.cancellationSuccessHeadlinePlural}`;
                const cancellationSuccessAlert: Alert = {
                    article: {
                        headline,
                        sublines: [],
                    },
                    alertType: 'CANCELLATION_SUCCESS',
                };
                alerts.push(
                    <GenericAlert
                        key="cancellation-success"
                        alert={cancellationSuccessAlert}
                        csaTagProps={generateCsaTagProps(`cancellation-success${pillPackSuffix}`, 'alert', 'cancellation-success')}
                    />
                );
            } else if (this.props.cancellationResult.status === 'pending') {
                const cancellationPendingAlert: Alert = {
                    article: {
                        headline: cancelForm.cancellationPendingHeadline,
                        sublines: [cancelForm.cancellationPendingSubline],
                    },
                    alertType: 'CANCELLATION_PENDING',
                };
                alerts.push(
                    <GenericAlert
                        key="cancellation-pending"
                        alert={cancellationPendingAlert}
                        csaTagProps={generateCsaTagProps(`cancellation-pending${pillPackSuffix}`, 'alert', 'cancellation-pending')}
                    />
                );
            }
        }

        if (paymentMethodUpdated) {
            const paymentMethodUpdateAlert: Alert = {
                article: {
                    headline: orderSummaryPage.orderSummaryPageLabels.paymentLabels.paymentMethodUpdatedTitle,
                    sublines: [orderSummaryPage.orderSummaryPageLabels.paymentLabels.paymentMethodUpdatedText],
                },
                alertType: 'PAYMENT_METHOD_UPDATE',
            };
    
            alerts.push(
                <GenericAlert
                    key="payment-method-update"
                    alert={paymentMethodUpdateAlert}
                    csaTagProps={generateCsaTagProps('payment-method-update', 'alert', 'payment-method-update')}
                />
            );
        } else if (orderSummaryPage.alerts && orderSummaryPage.alerts.length > 0) {
            alerts.push(
                ...orderSummaryPage.alerts.map((alert, index) => {
                    if (alert.alertType === 'PAYMENT') {
                        return (
                            <PaymentDeclineAlert
                                key={`alert-${index}`}
                                paymentLabels={orderSummaryPage.orderSummaryPageLabels.paymentLabels}
                                gqlOrderId={gqlOrderId}
                                alert={alert}
                                csaTagProps={generateCsaTagProps(`payment-decline-alert${pillPackSuffix}`, 'alert', `payment-decline-alert-${index}`)}
                                paymentInformation={orderSummaryPage.paymentInformation}
                            />
                        );
                    } else {
                        return (
                            <GenericAlert
                                key={`alert-${index}`}
                                alert={alert}
                                csaTagProps={generateCsaTagProps(`${alert.alertType.toLowerCase()}${pillPackSuffix}`, 'alert', `${alert.alertType.toLowerCase()}-${index}`)}
                            />
                        );
                    }
                })
            );
        }
        return alerts;
    };

}

OrderDetailV2.contextType = I18NContext;

export default OrderDetailV2;
